import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, GraduationCap, Home, ShieldCheck, Users, Stethoscope, Wallet, Briefcase } from "lucide-react";
import { useDeployment } from "@/lib/DeploymentContext";

const FAMILY_IMG =
  "https://images.unsplash.com/photo-1609253925210-c64083102ae5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const SENIOR_IMG =
  "https://images.unsplash.com/photo-1668701065787-2605db5e4938?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const family = [
  {
    icon: GraduationCap,
    title: "Employment & Education",
    body: "Direct enrollment into Lee College high-demand certificates — medical billing, logistics, welding — paired with Workforce Solutions childcare subsidies during class hours.",
  },
  {
    icon: Home,
    title: "Housing Security",
    body: "Transition families from week-to-week motel stays and overcrowded shares into stable leases or first-time homeownership preparation.",
  },
  {
    icon: Users,
    title: "Child Development",
    body: "Head Start enrollment and after-school program placement so a mother can attend training or work without childcare anxiety.",
  },
  {
    icon: Stethoscope,
    title: "Whole-Family Health",
    body: "Medicaid / CHIP enrollment, primary care assignment via Harris Health, and dental & preventative care for every member.",
  },
];

const senior = [
  {
    icon: Heart,
    title: "Health & Safety",
    body: "Chronic disease management, prescription assistance, and home safety assessments to prevent falls — the single largest driver of senior decline.",
  },
  {
    icon: ShieldCheck,
    title: "Financial Protection",
    body: "Shielding seniors from predatory scams. Maximizing SNAP benefits. Securing LIHEAP utility assistance before the summer heat lock-in.",
  },
  {
    icon: Wallet,
    title: "Social Connection",
    body: "Basic tech training to close the digital divide, plus pairing with community centers, faith communities, and weekly visit check-ins to fight isolation.",
  },
  {
    icon: Briefcase,
    title: "Transit & Continuity",
    body: "Specialized Harris County Transit scheduling so monthly doctor visits and pharmacy pickups never lapse.",
  },
];

function Block({ Icon, title, body, accent }) {
  return (
    <div className="group bg-white border border-[#CBD5E1] p-6 lg:p-7 transition-colors hover:bg-[#0A192F] hover:border-[#0A192F]">
      <div className="flex items-start gap-4">
        <span
          className="shrink-0 grid place-items-center w-10 h-10 border"
          style={{ borderColor: accent, color: accent }}
        >
          <Icon className="h-5 w-5 group-hover:text-[#D4AF37] transition-colors" />
        </span>
        <div>
          <h4 className="font-display text-lg text-[#0A192F] group-hover:text-[#F9F7F1] transition-colors">
            {title}
          </h4>
          <p className="mt-2 text-sm text-[#475569] leading-relaxed group-hover:text-[#CBD5E1] transition-colors">
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TwoProngedStrategy() {
  const d = useDeployment();
  return (
    <section
      id="strategy"
      data-testid="section-two-pronged-strategy"
      className="bg-white py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                02 — The Two-Pronged Strategy
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              One household. <em className="italic">Two generations.</em> One connector.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-[#334155] text-base leading-relaxed">
              {d.strategyOpeningLine}
              households are multigenerational — a working mother, her children, and an
              aging parent under one roof. We serve them together, with two parallel
              tracks operated by the same connector.
            </p>
          </div>
        </div>

        <Tabs defaultValue="family" className="w-full">
          <TabsList
            data-testid="strategy-tabs"
            className="bg-[#F9F7F1] border border-[#CBD5E1] rounded-none h-auto p-0 grid grid-cols-2 w-full lg:w-auto lg:inline-grid"
          >
            <TabsTrigger
              value="family"
              data-testid="tab-family"
              className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-[#F9F7F1] font-mono text-xs uppercase tracking-widest py-3 px-6 text-[#334155]"
            >
              Whole-Family Track
            </TabsTrigger>
            <TabsTrigger
              value="senior"
              data-testid="tab-senior"
              className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-[#F9F7F1] font-mono text-xs uppercase tracking-widest py-3 px-6 text-[#334155]"
            >
              Senior Track
            </TabsTrigger>
          </TabsList>

          <TabsContent value="family" className="mt-8">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#CBD5E1]">
                  <img
                    src={FAMILY_IMG}
                    alt={d.strategyImageAlt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-[#0A192F] text-[#F9F7F1] px-5 py-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                      Building Generational Wealth
                    </div>
                    <div className="font-display text-xl">
                      Single mothers, children, and the path through Lee College.
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                {family.map((b) => (
                  <Block key={b.title} Icon={b.icon} title={b.title} body={b.body} accent="#0A192F" />
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="senior" className="mt-8">
            <div className="grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-5">
                <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#CBD5E1]">
                  <img
                    src={SENIOR_IMG}
                    alt="Senior women smiling together"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-[#8B0000] text-[#F9F7F1] px-5 py-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                      Preserving Dignity & Independence
                    </div>
                    <div className="font-display text-xl">
                      Aging in place — safely, socially, and with daily support.
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                {senior.map((b) => (
                  <Block key={b.title} Icon={b.icon} title={b.title} body={b.body} accent="#8B0000" />
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
